import React, { useEffect, useRef } from "react";
import {
  ArrayInput,
  AutocompleteInput,
  BooleanInput,
  ReferenceInput,
  SelectInput,
  SimpleFormIterator,
  TextInput,
  maxLength,
  required,
  useSimpleFormIteratorItem,
} from "react-admin";
import { Grid } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { settings } from "app";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import { employeeRequests } from "api/employeeApi";
import { TravelerKindChoices } from "utils/choices";
import { BusinessTripTypes, DomesticTripCategories, TravelerKinds } from "utils/constants";
import { getDisplayName } from "utils/helpers";
import { validateChecked, validateTravelers } from "components/Leaves/Requests/utils/validations";

// The fields each kind owns. Switching kind clears everything the new kind does not own, so a stale
// value is never submitted — `fullName` survives External ↔ Student because both kinds use it.
const FIELDS_BY_KIND = {
  [TravelerKinds.Employee]: ["employeeId"],
  [TravelerKinds.External]: [
    "fullName",
    "contractNumber",
    "contractDate",
    "hasOtherEmployer",
    "employerConsentProvided",
  ],
  [TravelerKinds.Student]: ["fullName", "faculty", "facultyNumber"],
  [TravelerKinds.Doctoral]: ["fullName", "faculty"],
};

const ALL_TRAVELER_FIELDS = [...new Set(Object.values(FIELDS_BY_KIND).flat())];

// The two consent flags are booleans on the backend, so they clear to false rather than null.
const CLEARED_VALUES = { hasOtherEmployer: false, employerConsentProvided: false };

const kindChoices = kinds => TravelerKindChoices.filter(choice => kinds.includes(choice.id));

// Hoisted so each SelectInput keeps a stable choices reference across renders.
const STAFF_KINDS = kindChoices([TravelerKinds.Employee]);
const JURY_KINDS = kindChoices([TravelerKinds.External]);
const STUDENT_KINDS = kindChoices([TravelerKinds.Doctoral, TravelerKinds.Student]);
const ABROAD_KINDS = kindChoices([
  TravelerKinds.Employee,
  TravelerKinds.External,
  TravelerKinds.Doctoral,
  TravelerKinds.Student,
]);
const DEFAULT_KINDS = kindChoices([TravelerKinds.Employee, TravelerKinds.External]);

// The staff and jury categories admit one kind each; the student category covers both a student and
// a doctoral candidate. An abroad trip has no category, so the kind alone shapes the payload.
const resolveKindChoices = (businessTripType, domesticTripCategory) => {
  if (!businessTripType) return DEFAULT_KINDS;
  if (businessTripType !== BusinessTripTypes.Internal) return ABROAD_KINDS;
  if (domesticTripCategory === DomesticTripCategories.Staff) return STAFF_KINDS;
  if (domesticTripCategory === DomesticTripCategories.Jury) return JURY_KINDS;
  if (domesticTripCategory === DomesticTripCategories.Student) return STUDENT_KINDS;
  return DEFAULT_KINDS;
};

const TravelerRowItem = () => {
  const { index } = useSimpleFormIteratorItem();
  const { setValue } = useFormContext();
  const travelerKind = useWatch({ name: `travelers.${index}.travelerKind` });
  const hasOtherEmployer = useWatch({ name: `travelers.${index}.hasOtherEmployer` });
  const businessTripType = useWatch({ name: "businessTripType" });
  const domesticTripCategory = useWatch({ name: "domesticTripCategory" });
  const isDomestic = businessTripType === BusinessTripTypes.Internal;
  const isJury = isDomestic && domesticTripCategory === DomesticTripCategories.Jury;
  const availableKinds = resolveKindChoices(businessTripType, domesticTripCategory);
  const consentFirstRender = useRef(true);
  const kindFirstRender = useRef(true);

  // Clear the fields the new kind does not own. Skipped on mount so a loaded row keeps its values.
  useEffect(() => {
    if (kindFirstRender.current) {
      kindFirstRender.current = false;
      return;
    }
    const ownedFields = FIELDS_BY_KIND[travelerKind] ?? [];
    ALL_TRAVELER_FIELDS.filter(field => !ownedFields.includes(field)).forEach(field =>
      setValue(`travelers.${index}.${field}`, CLEARED_VALUES[field] ?? null),
    );
  }, [travelerKind, index, setValue]);

  // Must also run on mount, so a row whose kind the category no longer allows never renders blank.
  useEffect(() => {
    if (availableKinds.some(choice => choice.id === travelerKind)) return;
    setValue(`travelers.${index}.travelerKind`, availableKinds[0].id);
  }, [availableKinds, travelerKind, index, setValue]);

  useEffect(() => {
    if (consentFirstRender.current) {
      consentFirstRender.current = false;
      return;
    }
    if (!hasOtherEmployer) setValue(`travelers.${index}.employerConsentProvided`, false);
  }, [hasOtherEmployer, index, setValue]);

  // An abroad trip carries no category, so the contract data is what marks a traveler as jury-like
  // — the backend requires it there for the same reason it requires it of a domestic jury member.
  const isJuryLike = isJury || !isDomestic;
  const validateJuryContract = isJuryLike ? required() : undefined;
  // The faculty number is what separates a student from a doctoral candidate once the trip is saved.
  const isStudent = travelerKind === TravelerKinds.Student;

  const renderKindFields = () => {
    switch (travelerKind) {
      case TravelerKinds.External:
        return (
          <>
            <Grid
              item
              xs={12}
              md={5}
            >
              <TextInput
                source="fullName"
                label="Име на външното лице"
                fullWidth
                validate={required()}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={2}
            >
              <TextInput
                source="contractNumber"
                label="№ на договор"
                fullWidth
                validate={validateJuryContract}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={2}
            >
              <CustomDateInput
                source="contractDate"
                label="Дата на договор"
                noTime
                validate={validateJuryContract}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
            >
              <BooleanInput
                source="hasOtherEmployer"
                label="Има друг работодател"
                defaultValue={false}
              />
            </Grid>
            {hasOtherEmployer && (
              <Grid
                item
                xs={12}
                md={4}
              >
                <BooleanInput
                  source="employerConsentProvided"
                  label="Предоставено съгласие от работодателя"
                  defaultValue={false}
                  validate={isJuryLike ? validateChecked("Необходимо е съгласие от другия работодател!") : undefined}
                />
              </Grid>
            )}
          </>
        );

      case TravelerKinds.Doctoral:
      case TravelerKinds.Student:
        return (
          <>
            <Grid
              item
              xs={12}
              md={4}
            >
              <TextInput
                source="fullName"
                label={isStudent ? "Име на студента" : "Име на докторанта"}
                fullWidth
                validate={required()}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={isStudent ? 3 : 5}
            >
              <TextInput
                source="faculty"
                label="Факултет"
                fullWidth
                validate={[required(), maxLength(200)]}
              />
            </Grid>
            {isStudent && (
              <Grid
                item
                xs={12}
                md={2}
              >
                <TextInput
                  source="facultyNumber"
                  label="Факултетен номер"
                  fullWidth
                  validate={[required(), maxLength(50)]}
                />
              </Grid>
            )}
          </>
        );

      default:
        return (
          <Grid
            item
            xs={12}
            md={9}
          >
            <ReferenceInput
              source="employeeId"
              reference={employeeRequests.getEmployeesSelect()}
              filter={{ filterByUserNode: true }}
            >
              <AutocompleteInput
                label="Служител"
                fullWidth
                optionValue="id"
                optionText={getDisplayName}
                filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                matchSuggestion={() => true}
                validate={required()}
              />
            </ReferenceInput>
          </Grid>
        );
    }
  };

  return (
    <Grid
      container
      spacing={1}
    >
      <Grid
        item
        xs={12}
        md={3}
      >
        <SelectInput
          source="travelerKind"
          label="Тип"
          choices={availableKinds}
          defaultValue={availableKinds[0].id}
          validate={required()}
          fullWidth
        />
      </Grid>
      {renderKindFields()}
    </Grid>
  );
};

const TravelersRow = () => (
  <Grid
    item
    xs={12}
    p={0.5}
  >
    <ArrayInput
      source="travelers"
      label="Пътуващи"
      defaultValue={[{}]}
      validate={validateTravelers}
    >
      <SimpleFormIterator sx={settings.style.iteratorWithoutGap}>
        <TravelerRowItem />
      </SimpleFormIterator>
    </ArrayInput>
  </Grid>
);

export default TravelersRow;
